import { IfAbility } from '@efdigital/ll-addon-auth';
import type { File, Folder } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { Heading } from '@efdigital/ll-addon-ui';
import {
  EditContext,
  EditingStoppedReason,
} from '@efdigital/ll-admin-v2/components/edit-context.gts';
import FileBrowser from '@efdigital/ll-admin-v2/components/files/file-browser.gts';
import FileUploader from '@efdigital/ll-admin-v2/components/files/file-uploader.gts';
import FolderCreate from '@efdigital/ll-admin-v2/components/files/folder-create.gts';
import { findFolderAndContents } from '@efdigital/ll-admin-v2/data/builders/content/files';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface LibraryFoldersIndexModel {
  path: string;
}

export default class LibraryFoldersIndex extends Component<{
  Args: { model: LibraryFoldersIndexModel };
}> {
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked isFileModalOpen = false;
  @tracked isFolderModalOpen = false;
  @tracked folderRecord: Folder | null = null;

  get currentPath() {
    return this.args.model.path;
  }

  /**
   * Check if we can create folders/files at the current path.
   * Creation is not allowed at the root level (|).
   */
  get allowCreate() {
    return this.currentPath !== '|';
  }

  onFileSelected = (file: File) => {
    this.router.transitionTo('library.folders.file', file.id!);
    return Promise.resolve();
  };

  navigateToFolder = (path: string) => {
    this.router.transitionTo('library.folders.index', {
      queryParams: { parent: path },
    });
  };

  onFolderSelected = (folder: Folder) => {
    this.navigateToFolder(folder.id!);
  };

  showFileModal = () => {
    this.isFileModalOpen = true;
  };

  hideFileModal = () => {
    this.isFileModalOpen = false;
  };

  showFolderModal = () => {
    this.folderRecord = this.store.createRecord<Folder>(
      DataTypeContent.Folder,
      {
        name: '',
        parent: this.currentPath,
      }
    );
    this.isFolderModalOpen = true;
  };

  hideFolderModal = (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Saved) {
      const newFolderId = this.folderRecord?.id;
      this.isFolderModalOpen = false;
      this.folderRecord = null;
      this.router.transitionTo('library.folders.index', {
        queryParams: { parent: newFolderId! },
      });
    } else {
      this.isFolderModalOpen = false;
      this.store.unloadRecord(this.folderRecord);
      this.folderRecord = null;
      return;
    }
  };

  fileUploaded = (file: File) => {
    this.isFileModalOpen = false;

    const parentFolderId = file.parent;
    if (parentFolderId) {
      // Reload the parent folder
      void this.store.request(findFolderAndContents(parentFolderId));
    }
  };

  <template>
    <div class="container py-5">
      <Heading
        @text="Private Files"
        @secondary="Store documents securely. These folders can be embedded within pages."
      />

      <FileBrowser
        @path={{this.currentPath}}
        @startFrom="|"
        @navigateToFolder={{this.navigateToFolder}}
        @selectFile={{this.onFileSelected}}
        @selectFolder={{this.onFolderSelected}}
      />

      <IfAbility @ability="library:files" @atLeast="editor">
        <:if>
          <div class="text-center mt-5">
            {{#if this.allowCreate}}
              <button
                type="button"
                class="btn btn-primary btn-md"
                {{on "click" this.showFileModal}}
              >
                <iconify-icon inline icon="ph:file-plus" />
                Add File
              </button>

              <button
                type="button"
                class="btn btn-primary btn-md"
                {{on "click" this.showFolderModal}}
              >
                <iconify-icon inline icon="ph:folder-plus" />
                Add Folder
              </button>
            {{else}}
              <p class="text-muted">Navigate into a folder to add files or
                create subfolders.</p>
            {{/if}}
          </div>
        </:if>
      </IfAbility>

      <BsModal
        @open={{this.isFileModalOpen}}
        @onHidden={{this.hideFileModal}}
        @size="xl"
        @backdropClose={{false}}
        as |modal|
      >
        <modal.header>
          <h2 class="modal-title fs-5">Add File</h2>
        </modal.header>
        <modal.body>
          <FileUploader
            @parentFolderId={{this.currentPath}}
            @onFileUploaded={{this.fileUploaded}}
          />
        </modal.body>
      </BsModal>

      <BsModal
        @open={{this.isFolderModalOpen}}
        @onHidden={{fn this.hideFolderModal EditingStoppedReason.Cancelled}}
        @size="lg"
        @backdropClose={{false}}
        as |modal|
      >
        <modal.header>
          <h2 class="modal-title fs-5">Add Folder</h2>
        </modal.header>
        <modal.body>
          {{#if this.folderRecord}}
            <EditContext
              @onEditingStopped={{this.hideFolderModal}}
              as |context|
            >
              <FolderCreate @item={{this.folderRecord}} @context={{context}} />
            </EditContext>
          {{/if}}
        </modal.body>
      </BsModal>
    </div>
  </template>
}
